import { GetStaticPropsResult, GetStaticPathsResult } from 'next'
import { ParsedUrlQuery } from 'querystring'

import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import {
  SanityProductCatalogueQuery,
  SanityPrintProductPage,
  SanityProductFragment,
} from '@data/sanity/queries/types/product'
import { getShopifyDomain } from '@lib/shopify/client'
import { getPreview } from '@lib/sanity/client'
import { getPrintProductPage, getProductCatalogue } from '@lib/sanity/product'
import { useViewContentFacebookEvent } from '@lib/facebook'
import { Locale } from '@lib/language'
import { useVariantMetadata } from '@lib/metadata'
import { useProductWithInventory } from '@lib/product'
import { ProductVariantSelectionContextProvider } from '@lib/product-variant-selection-context'
import { getReviews, Reviews } from '@lib/review'
import { CustomStaticPropsContext } from '@lib/routes'
import { useProductSchema } from '@lib/schema'
import { useSetPrintShop } from '@lib/shop'

import Modules from '@modules/modules'
import Layout from '@components/layout'

interface StaticYearShopProductPageParams extends ParsedUrlQuery {
  slug: string
  id: string
}

interface YearShopProductPageProps {
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
  reviews?: Reviews
}

interface YearShopProductPageContentProps {
  product: SanityProductFragment
  locale: Locale
  page: SanityPrintProductPage
  site: SanitySiteFragment
  reviews?: Reviews
}

const YearShopProductPageContent = ({
  product,
  locale,
  page,
  site,
  reviews,
}: YearShopProductPageContentProps) => {
  useSetPrintShop(page.printShop)

  // Get product schema
  const productSchema = useProductSchema(site, product)

  // Trigger Facebook event
  useViewContentFacebookEvent(
    locale,
    product.title,
    site.settings.facebookEvents
  )

  // Set product variant metadata
  useVariantMetadata()

  if (!page.privateModules) {
    return null
  }

  return (
    <Layout page={page} site={site} productSchema={productSchema}>
      <Modules
        modules={page.privateModules}
        cartSettings={site.cart}
        product={product}
        reviews={reviews}
        printShop={page.printShop}
      />
    </Layout>
  )
}

/**
 * The product page.
 */
const YearShopProductPage = ({
  locale,
  page,
  site,
  reviews,
}: YearShopProductPageProps) => {
  const product = useProductWithInventory(page, locale)

  return (
    <ProductVariantSelectionContextProvider product={product}>
      <YearShopProductPageContent
        product={product}
        locale={locale}
        page={page}
        site={site}
        reviews={reviews}
      />
    </ProductVariantSelectionContextProvider>
  )
}

export async function getStaticProps({
  locale,
  params,
  preview,
  previewData,
}: CustomStaticPropsContext<StaticYearShopProductPageParams>): Promise<
  GetStaticPropsResult<YearShopProductPageProps>
> {
  const productPage = await getPrintProductPage(
    locale,
    params?.id ?? '',
    params?.slug ?? '',
    getPreview(preview, previewData)
  )

  if (!productPage.page) {
    return { notFound: true }
  }

  const productCatalogue = await getProductCatalogue(locale)

  // Get Stamped.io reviews
  const shopifyDomain = getShopifyDomain(locale)
  const reviews = await getReviews(
    productPage.page.modules ?? [],
    shopifyDomain,
    productPage.site.settings.stampedApiKey,
    productPage.page.product.id
  )

  return {
    props: {
      locale,
      page: productPage.page,
      site: productPage.site,
      productCatalogue,
      reviews,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticYearShopProductPageParams>
> {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default YearShopProductPage
